import * as React from "react";
import { cx } from "../internal/cx.js";
import { formatStoredSsnDisplay, isStoredSsnDisplayValue } from "../internal/ssn-format.js";
import { Button } from "./button.js";
import { Label, type LabelSize } from "./label.js";

export type InputSize = "sm" | "md" | "lg" | "xl";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix" | "size"> {
  /**
   * Error message rendered below the input. When truthy, also flips
   * `aria-invalid` and applies the `cw-v2-input--error` styling. Pass an
   * empty string with `invalid` to mark invalid without a visible message.
   */
  error?: string;
  /** Standalone invalid flag without a message. Mostly used by `<FormField.Input>` which already renders its own error region. */
  invalid?: boolean;
  /** Helper text rendered when there is no error. Auto-id'd into `aria-describedby`. */
  helperText?: string;
  /** Renders a `<Label htmlFor={id}>` above the input. */
  label?: React.ReactNode;
  /** Suffix "(Optional)" on the label. Ignored if `label` is absent. */
  optional?: boolean;
  /** Visual element rendered inside the input shell, before the input. */
  prefix?: React.ReactNode;
  /** Visual element rendered inside the input shell, after the input. */
  suffix?: React.ReactNode;
  size?: InputSize;
  /**
   * Mark the field as capturing sensitive PII so PostHog session
   * replay masks it. See `cross-cutting/analytics.mdc` "PII & session
   * replay". `type="password"` is auto-masked and does not need this.
   */
  sensitive?: boolean;
}

function getLabelSize(size: InputSize): LabelSize {
  return size === "sm" ? "sm" : size === "xl" ? "lg" : "md";
}

type KeyboardHint = {
  autoCapitalize?: string;
  autoComplete?: string;
  autoCorrect?: string;
  inputMode?: React.HTMLAttributes<HTMLElement>["inputMode"];
  spellCheck?: boolean;
};

/**
 * Sensible mobile-keyboard defaults derived from the input `type`.
 * Consumers can always override any individual attribute explicitly.
 */
const KEYBOARD_HINTS: Record<string, KeyboardHint> = {
  email: {
    autoCapitalize: "none",
    autoComplete: "email",
    autoCorrect: "off",
    inputMode: "email",
    spellCheck: false,
  },
  url: {
    autoCapitalize: "none",
    autoComplete: "url",
    autoCorrect: "off",
    inputMode: "url",
    spellCheck: false,
  },
  tel: {
    autoComplete: "tel",
    inputMode: "tel",
  },
  search: {
    autoCapitalize: "none",
    autoCorrect: "off",
    inputMode: "search",
    spellCheck: false,
  },
  password: {
    autoCapitalize: "none",
    autoCorrect: "off",
    spellCheck: false,
  },
  number: {
    inputMode: "decimal",
  },
};

function hasValue(
  value:
    | React.InputHTMLAttributes<HTMLInputElement>["value"]
    | React.InputHTMLAttributes<HTMLInputElement>["defaultValue"],
): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && String(value).length > 0;
}

function formatDigits(value: string, maxLength: number): string {
  return value.replace(/\D/g, "").slice(0, maxLength);
}

function clampNumber(value: number, min?: number | string, max?: number | string): number {
  const numericMin = min === undefined || min === "" ? undefined : Number(min);
  const numericMax = max === undefined || max === "" ? undefined : Number(max);
  let next = value;
  if (numericMin !== undefined && Number.isFinite(numericMin)) next = Math.max(next, numericMin);
  if (numericMax !== undefined && Number.isFinite(numericMax)) next = Math.min(next, numericMax);
  return next;
}

/** Format an EIN as `XX-XXXXXXX`. */
export function formatEIN(value: string): string {
  const digits = formatDigits(value, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

/** Format a US phone as `(XXX) XXX-XXXX`. */
export function formatPhone(value: string): string {
  const digits = formatDigits(value, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Format an SSN as `XXX-XX-XXXX`. */
export function formatSSN(value: string): string {
  const digits = formatDigits(value, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function useFormattedTextValue(
  value: string | undefined,
  defaultValue: string | undefined,
  formatter: (value: string) => string,
  onValueChange: ((value: string) => void) | undefined,
) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState(() => formatter(defaultValue ?? ""));
  const displayValue = isControlled ? formatter(value) : internalValue;
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatter(event.target.value);
      if (!isControlled) setInternalValue(formatted);
      onValueChange?.(formatted);
    },
    [formatter, isControlled, onValueChange],
  );
  return { displayValue, handleChange };
}

/**
 * Text input with optional label, prefix/suffix, helper text and error
 * message. The component owns its accessibility wiring (label↔input via
 * `htmlFor`/`id`, `aria-describedby` → helper or error region,
 * `aria-invalid` when in an error state).
 *
 * For controlled formatted inputs (currency, phone, SSN, EIN, decimal),
 * use the dedicated specialised exports below.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    autoCapitalize,
    autoComplete,
    autoCorrect,
    className,
    defaultValue,
    disabled,
    error,
    helperText,
    id,
    inputMode,
    invalid,
    label,
    optional = false,
    prefix,
    readOnly,
    sensitive,
    size = "md",
    spellCheck,
    suffix,
    type = "text",
    value,
    ...props
  },
  ref,
) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const isInvalid = Boolean(error) || invalid === true;
  const helperId = helperText && !error ? `${inputId}-helper` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const filled = hasValue(value) || hasValue(defaultValue);
  const hints = KEYBOARD_HINTS[type];

  return (
    <div className="cw-v2-input-field">
      {label ? (
        <Label htmlFor={inputId} optional={optional} size={getLabelSize(size)}>
          {label}
        </Label>
      ) : null}
      <div
        className={cx(
          "cw-v2-input",
          `cw-v2-input--${size}`,
          isInvalid && "cw-v2-input--error",
          disabled && "cw-v2-input--disabled",
          filled && "cw-v2-input--filled",
          readOnly && "cw-v2-input--readonly",
          className,
        )}
      >
        {prefix ? <span className="cw-v2-input__affix">{prefix}</span> : null}
        <input
          ref={ref}
          aria-describedby={errorId ?? helperId}
          aria-invalid={isInvalid || undefined}
          autoCapitalize={autoCapitalize ?? hints?.autoCapitalize}
          autoComplete={autoComplete ?? hints?.autoComplete}
          autoCorrect={autoCorrect ?? hints?.autoCorrect}
          className="cw-v2-input__control"
          data-sensitive={sensitive ? "true" : undefined}
          defaultValue={defaultValue}
          disabled={disabled}
          id={inputId}
          inputMode={inputMode ?? hints?.inputMode}
          readOnly={readOnly}
          spellCheck={spellCheck ?? hints?.spellCheck}
          type={type}
          value={value}
          {...props}
        />
        {suffix ? <span className="cw-v2-input__affix">{suffix}</span> : null}
      </div>
      {error ? (
        <p className="cw-v2-input__message cw-v2-input__message--error" id={errorId}>
          {error}
        </p>
      ) : helperText ? (
        <p className="cw-v2-input__message" id={helperId}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
Input.displayName = "Input";

export interface NumberInputProps extends Omit<InputProps, "type" | "value"> {
  onValueChange?: (value: number | null) => void;
  value?: number | string;
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput({ max, min, onChange, onValueChange, ...props }, ref) {
    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        const parsed = Number(raw);
        const numeric =
          raw === "" || !Number.isFinite(parsed) ? null : clampNumber(parsed, min, max);
        onValueChange?.(numeric);
        onChange?.(event);
      },
      [max, min, onChange, onValueChange],
    );
    return (
      <Input
        ref={ref}
        inputMode="decimal"
        max={max}
        min={min}
        onChange={handleChange}
        type="number"
        {...props}
      />
    );
  },
);
NumberInput.displayName = "NumberInput";

export interface DateInputProps extends Omit<InputProps, "type"> {
  onValueChange?: (value: string) => void;
}

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { max, min, onChange, onValueChange, ...props },
  ref,
) {
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange?.(event.target.value);
      onChange?.(event);
    },
    [onChange, onValueChange],
  );
  return <Input ref={ref} max={max} min={min} onChange={handleChange} type="date" {...props} />;
});
DateInput.displayName = "DateInput";

export interface CurrencyInputProps
  extends Omit<
    InputProps,
    "defaultValue" | "inputMode" | "max" | "min" | "onChange" | "type" | "value"
  > {
  defaultValue?: number | null;
  fractionDigits?: number;
  locale?: string;
  max?: number;
  min?: number;
  onValueChange?: (value: number | null) => void;
  value?: number | null;
}

/** What the user has literally typed, plus the number it currently denotes. */
type CurrencyDraft = { text: string; value: number | null };

/**
 * Split a cleaned currency string into the parts needed to both display it and
 * read a number out of it, keeping the fractional part EXACTLY as typed.
 *
 * Round-tripping the whole string through `Number` (`"12." -> 12 -> "12"`)
 * swallows the decimal point the moment it is typed, which makes cents
 * impossible to enter in a controlled field. Keeping the digits after the dot
 * as a string is what lets `12.` and `12.50` survive a re-render.
 */
function parseCurrencyDraft(raw: string, fractionDigits: number) {
  const negative = raw.trimStart().startsWith("-");
  const unsigned = raw.replace(/-/g, "");
  const dotIndex = unsigned.indexOf(".");
  const acceptsFraction = fractionDigits > 0;
  const intDigits = (dotIndex === -1 ? unsigned : unsigned.slice(0, dotIndex)).replace(/\D/g, "");
  // A whole-dollar field keeps the dot but drops every digit behind it, so the
  // cents a user types are visibly ignored instead of being folded into the
  // integer part (`75000.00` stays $75,000 and never becomes $7,500,000).
  const fracDigits =
    !acceptsFraction || dotIndex === -1
      ? ""
      : unsigned
          .slice(dotIndex + 1)
          .replace(/\D/g, "")
          .slice(0, fractionDigits);
  const hasDigits = intDigits !== "" || fracDigits !== "";
  return {
    fracDigits,
    hasDot: dotIndex !== -1,
    intDigits,
    negative,
    value: hasDigits
      ? Number(`${negative ? "-" : ""}${intDigits || "0"}.${fracDigits || "0"}`)
      : null,
  };
}

export const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  function CurrencyInput(
    {
      defaultValue,
      fractionDigits = 0,
      locale = "en-US",
      max,
      min,
      onBlur,
      onValueChange,
      prefix = "$",
      value,
      ...props
    },
    ref,
  ) {
    const formatter = React.useMemo(
      () =>
        new Intl.NumberFormat(locale, {
          maximumFractionDigits: fractionDigits,
          minimumFractionDigits: fractionDigits,
        }),
      [fractionDigits, locale],
    );
    const groupFormatter = React.useMemo(
      () => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
      [locale],
    );
    const formatCurrency = React.useCallback(
      (next: number | null | undefined): string => {
        if (next === undefined || next === null || Number.isNaN(next)) return "";
        return formatter.format(clampNumber(next, min, max));
      },
      [formatter, max, min],
    );
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = React.useState<number | null>(
      defaultValue ?? null,
    );
    // Held only while the typed text and the committed value still agree; any
    // value the parent pushes back that we did not just emit wins over it.
    const [draft, setDraft] = React.useState<CurrencyDraft | null>(null);
    const currentValue = isControlled ? (value ?? null) : uncontrolledValue;
    const displayValue =
      draft && draft.value === currentValue ? draft.text : formatCurrency(currentValue);
    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const cleaned = event.target.value.replace(/[^\d.-]/g, "");
        const {
          fracDigits,
          hasDot,
          intDigits,
          negative,
          value: parsed,
        } = parseCurrencyDraft(cleaned, fractionDigits);
        const next = parsed === null ? null : clampNumber(parsed, min, max);
        // Clamping rewrote the number, so the typed text no longer describes it.
        const text =
          next !== parsed
            ? formatCurrency(next)
            : `${negative ? "-" : ""}${
                intDigits === "" ? (hasDot ? "0" : "") : groupFormatter.format(Number(intDigits))
              }${hasDot ? `.${fracDigits}` : ""}`;
        setDraft({ text, value: next });
        if (!isControlled) setUncontrolledValue(next);
        onValueChange?.(next);
      },
      [formatCurrency, fractionDigits, groupFormatter, isControlled, max, min, onValueChange],
    );
    const handleBlur = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        // Drop the draft so the committed number renders canonically (`12.5`
        // settles to `12.50`); the value itself never changes here.
        setDraft(null);
        onBlur?.(event);
      },
      [onBlur],
    );
    return (
      <Input
        ref={ref}
        autoComplete="off"
        inputMode="decimal"
        onBlur={handleBlur}
        onChange={handleChange}
        prefix={prefix}
        type="text"
        value={displayValue}
        {...props}
      />
    );
  },
);
CurrencyInput.displayName = "CurrencyInput";

type FormattedTextInputProps = Omit<
  InputProps,
  "defaultValue" | "inputMode" | "maxLength" | "onChange" | "type" | "value"
> & {
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  value?: string;
};

export type PhoneInputProps = FormattedTextInputProps;

export type SSNInputProps = FormattedTextInputProps & {
  /**
   * Masked or partial SSN returned from the server when editing an existing
   * record (e.g. `•••-••-3244`). Shows `***-**-3244` in a disabled field with
   * an **Edit** suffix until the user opts in to replace it.
   */
  storedValue?: string;
  /** Suffix link label when `storedValue` is shown. Defaults to `"Edit"`. */
  editLabel?: string;
  /** Fired when the user clicks **Edit** to enter a new SSN. */
  onUnlock?: () => void;
};

export type EINInputProps = FormattedTextInputProps;

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { defaultValue, onValueChange, value, ...props },
  ref,
) {
  const { displayValue, handleChange } = useFormattedTextValue(
    value,
    defaultValue,
    formatPhone,
    onValueChange,
  );
  return (
    <Input
      ref={ref}
      autoComplete="tel"
      inputMode="tel"
      maxLength={14}
      onChange={handleChange}
      type="tel"
      value={displayValue}
      {...props}
    />
  );
});
PhoneInput.displayName = "PhoneInput";

export const SSNInput = React.forwardRef<HTMLInputElement, SSNInputProps>(
  function SSNInput(props, ref) {
    // Remount when the on-file value changes so unlock state resets per record.
    return <SSNInputInner key={props.storedValue ?? ""} ref={ref} {...props} />;
  },
);
SSNInput.displayName = "SSNInput";

const SSNInputInner = React.forwardRef<HTMLInputElement, SSNInputProps>(function SSNInputInner(
  {
    defaultValue,
    editLabel = "Edit",
    onUnlock,
    onValueChange,
    storedValue,
    suffix,
    value,
    ...props
  },
  ref,
) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [unlocked, setUnlocked] = React.useState(false);

  React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const stored = storedValue?.trim() ?? "";
  const hasStoredOnFile = stored.length > 0 && isStoredSsnDisplayValue(stored);
  const showStored =
    hasStoredOnFile && !unlocked && (value === undefined || value === "" || value === null);

  const { displayValue, handleChange } = useFormattedTextValue(
    value,
    defaultValue,
    formatSSN,
    onValueChange,
  );

  const handleUnlock = React.useCallback(() => {
    setUnlocked(true);
    onUnlock?.();
    onValueChange?.("");
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [onUnlock, onValueChange]);

  if (showStored) {
    const storedDisplay = formatStoredSsnDisplay(stored);
    return (
      <Input
        ref={inputRef}
        autoComplete="off"
        inputMode="numeric"
        onChange={() => {}}
        placeholder="XXX-XX-XXXX"
        readOnly
        sensitive
        suffix={
          suffix ?? (
            <Button type="button" variant="link" size="sm" onClick={handleUnlock}>
              {editLabel}
            </Button>
          )
        }
        type="text"
        value={storedDisplay}
        disabled
        {...props}
      />
    );
  }

  return (
    <Input
      ref={inputRef}
      autoComplete="off"
      inputMode="numeric"
      maxLength={11}
      onChange={handleChange}
      placeholder="XXX-XX-XXXX"
      sensitive
      suffix={suffix}
      type="text"
      value={displayValue}
      {...props}
    />
  );
});
SSNInputInner.displayName = "SSNInputInner";

export const EINInput = React.forwardRef<HTMLInputElement, EINInputProps>(function EINInput(
  { defaultValue, onValueChange, value, ...props },
  ref,
) {
  const { displayValue, handleChange } = useFormattedTextValue(
    value,
    defaultValue,
    formatEIN,
    onValueChange,
  );
  return (
    <Input
      ref={ref}
      autoComplete="off"
      inputMode="numeric"
      maxLength={10}
      onChange={handleChange}
      placeholder="XX-XXXXXXX"
      type="text"
      value={displayValue}
      {...props}
    />
  );
});
EINInput.displayName = "EINInput";
